import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Form, Formik } from "formik";
import { useRetireAssetAssetAssetIdRetirePost } from "kubb";
import type React from "react";
import { useState } from "react";
import type { Asset } from "utils/object";
import * as Yup from "yup";
import { ErrorBanner } from "../../../components/Error/ErrorBanner.tsx";
import SingleDatePicker from "../../../components/Forms/SingleDatePicker";
import CancelButton from "../../../components/Modals/CancelButton.tsx";
import CreateButton from "../../../components/Modals/CreateButton.tsx";
import { useAssetRetirement } from "./useAssetRetirement";

const retirementSchema = Yup.object().shape({
  retirement_date: Yup.string()
    .required("Retirement date is required")
    .test("not-future", "Retirement date cannot be in the future", (value) => {
      if (!value) return false;
      return (
        dayjs(value).isBefore(dayjs().endOf("day")) ||
        dayjs(value).isSame(dayjs().endOf("day"))
      );
    }),
});

interface AssetRetireModalProps {
  asset: Asset;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function AssetRetireModal({
  asset,
  isOpen,
  onClose,
  onSuccess,
}: AssetRetireModalProps): React.ReactElement {
  const queryClient = useQueryClient();
  const [error, setError] = useState<Error | undefined>(undefined);
  const retireMutation = useRetireAssetAssetAssetIdRetirePost();

  // Use custom hook to get asset retirement data
  const { hasActiveLink, lastUninstallDate, lastUsedInfo } =
    useAssetRetirement(asset);

  const handleSubmit = async (values: { retirement_date: string | null }) => {
    if (!values.retirement_date) return;

    setError(undefined);

    try {
      await retireMutation.mutateAsync({
        asset_id: asset.asset_id,
        data: {
          retirement_date: values.retirement_date,
        },
        params: { company_id: asset.company_id },
      });
      await queryClient.invalidateQueries({
        queryKey: [{ url: "/asset" }, { asset_id: asset.asset_id }],
      });

      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-md rounded bg-white p-6 dark:bg-gray-800">
          <DialogTitle className="text-lg font-medium mb-4">
            Retire Asset
          </DialogTitle>

          <Formik
            initialValues={{
              retirement_date: lastUninstallDate
                ? dayjs(lastUninstallDate)
                    .tz("Europe/London")
                    .startOf("day")
                    .toISOString()
                : dayjs().tz("Europe/London").startOf("day").toISOString(),
            }}
            validationSchema={retirementSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, errors, touched, isSubmitting }) => {
              const retirementDateText = values.retirement_date
                ? `${dayjs(values.retirement_date).format("dddd D MMMM YYYY")} at 00:00:00`
                : "No date selected";

              return (
                <Form>
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Are you sure you want to retire asset{" "}
                      <strong>{asset.serial_number}</strong>?
                    </p>

                    {hasActiveLink && (
                      <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 rounded text-sm">
                        <strong>Warning:</strong> This asset is currently
                        installed at a position. You must uninstall it before
                        retiring.
                      </div>
                    )}

                    {!hasActiveLink && (
                      <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 rounded text-sm">
                        {!lastUsedInfo.hasBeenUsed ? (
                          <p>
                            <strong>Note:</strong> This asset has never been
                            installed at a position.
                          </p>
                        ) : lastUsedInfo.formattedDate ? (
                          <p>
                            <strong>Last used:</strong> This asset was last
                            uninstalled on{" "}
                            <strong>{lastUsedInfo.formattedDate}</strong>. The
                            retirement date has been set to this date by
                            default.
                          </p>
                        ) : (
                          <p>
                            <strong>Note:</strong> This asset is currently
                            installed but can be retired once uninstalled.
                          </p>
                        )}
                      </div>
                    )}

                    <SingleDatePicker
                      dateName="retirement_date"
                      label={{ label: "Retirement Date", size: "sm" }}
                      maxDate={dayjs().endOf("day")}
                      timeZone="Europe/London"
                      useStringValues={true}
                    />

                    {errors.retirement_date && touched.retirement_date && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors.retirement_date}
                      </p>
                    )}

                    {values.retirement_date && (
                      <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 font-medium">
                        Asset will be retired on {retirementDateText}.
                      </p>
                    )}

                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      The retirement date must be today or in the past. Retired
                      assets cannot be assigned to positions after their
                      retirement date.
                    </p>
                  </div>

                  <ErrorBanner error={error} />

                  <div className="flex gap-3 justify-end">
                    <CancelButton onClick={onClose}>Cancel</CancelButton>
                    <CreateButton
                      type="submit"
                      disabled={
                        isSubmitting || !values.retirement_date || hasActiveLink
                      }
                      loading={isSubmitting}
                      labelText={isSubmitting ? "Retiring..." : "Retire Asset"}
                    />
                  </div>
                </Form>
              );
            }}
          </Formik>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
